import React from 'react';
import { User, Mail, Calendar, Badge, Shield } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const UserProfileContent: React.FC = () => {
  const { user } = useAuth();

  return (
    <div className="p-6 space-y-6">
      {/* User Avatar and Basic Info */}
      <div className="flex flex-col items-center text-center pb-6 border-b border-gray-200">
        <div className="h-24 w-24 bg-primary-600 text-white rounded-full flex items-center justify-center text-2xl font-semibold mb-4 shadow-lg">
          {user?.avatar ? (
            <img 
              src={user.avatar} 
              alt={user.name} 
              className="h-24 w-24 rounded-full object-cover" 
            />
          ) : (
            <User className="h-10 w-10" />
          )}
        </div>
        <h3 className="text-xl font-semibold text-gray-900 mb-1">{user?.name || 'User'}</h3>
        <p className="text-sm text-gray-500 capitalize bg-gray-100 px-3 py-1 rounded-full">
          {user?.role?.replace('_', ' ') || 'Guest'}
        </p>
      </div>

      {/* Contact Information */}
      <div className="space-y-4">
        <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wide flex items-center space-x-2">
          <Mail className="h-4 w-4 text-primary-600" />
          <span>Contact Information</span>
        </h4>
        
        <div className="space-y-3 ml-6">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 w-2 h-2 bg-primary-400 rounded-full mt-2"></div>
            <div className="flex-1">
              <div className="text-sm font-medium text-gray-900">Email Address</div>
              <div className="text-sm text-gray-600 break-all">{user?.email || 'Not provided'}</div>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 w-2 h-2 bg-primary-400 rounded-full mt-2"></div>
            <div className="flex-1">
              <div className="text-sm font-medium text-gray-900">User ID</div>
              <div className="text-sm text-gray-600 font-mono">{user?.id || 'Not available'}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Academic/Professional Information */}
      {(user?.studentId || user?.employeeId) && (
        <div className="space-y-4">
          <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wide flex items-center space-x-2">
            <Badge className="h-4 w-4 text-primary-600" />
            <span>
              {user?.role === 'student' || user?.role === 'alumni' ? 'Academic Information' : 'Professional Information'}
            </span>
          </h4>
          
          <div className="space-y-3 ml-6">
            {user?.studentId && (
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-2 h-2 bg-primary-400 rounded-full mt-2"></div>
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900">Student ID (NIM)</div>
                  <div className="text-sm text-gray-600 font-mono">{user.studentId}</div>
                </div>
              </div>
            )}

            {user?.employeeId && (
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-2 h-2 bg-primary-400 rounded-full mt-2"></div>
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900">Employee ID (NIP)</div>
                  <div className="text-sm text-gray-600 font-mono">{user.employeeId}</div>
                </div>
              </div>
            )}

            {user?.graduationYear && (
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-2 h-2 bg-primary-400 rounded-full mt-2"></div>
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900">Graduation Year</div>
                  <div className="text-sm text-gray-600">{user.graduationYear}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* System Information */}
      <div className="space-y-4">
        <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wide flex items-center space-x-2">
          <Shield className="h-4 w-4 text-primary-600" />
          <span>Account Information</span>
        </h4>
        
        <div className="space-y-3 ml-6">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 w-2 h-2 bg-green-400 rounded-full mt-2"></div>
            <div className="flex-1">
              <div className="text-sm font-medium text-gray-900">Account Status</div>
              <div className="text-sm text-green-600 font-medium">Active</div>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 w-2 h-2 bg-primary-400 rounded-full mt-2"></div>
            <div className="flex-1">
              <div className="text-sm font-medium text-gray-900">Current Session</div>
              <div className="text-sm text-gray-600 capitalize">
                {user?.role?.replace('_', ' ') || 'Guest'} Portal
              </div>
            </div>
          </div>

          {user?.requestedRoles && user.requestedRoles.length > 0 && (
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-2 h-2 bg-yellow-400 rounded-full mt-2"></div>
              <div className="flex-1">
                <div className="text-sm font-medium text-gray-900">Pending Role Requests</div>
                <div className="text-sm text-gray-600">
                  {user.requestedRoles.map(role => role.replace('_', ' ')).join(', ')}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Last Login Info */}
      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
        <div className="flex items-center space-x-2 mb-2">
          <Calendar className="h-4 w-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-900">Session Information</span>
        </div>
        <div className="text-xs text-gray-600 space-y-1">
          <div>Login time: {new Date().toLocaleDateString('id-ID', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })}</div>
          <div>Browser: {navigator.userAgent.split(' ')[0] || 'Unknown'}</div>
        </div>
      </div>
    </div>
  );
};

export default UserProfileContent;